/**
 * Public contract for the Kalshi Compute forward curve, plus the client fetchers.
 *
 * Querying mirrors the KPOW index API (`/v1/cached/indexes/KPOW` + `/history`):
 * a snapshot endpoint and a history endpoint windowed by `start_ts` / `end_ts`,
 * with every timestamp in Unix SECONDS.
 *
 * This contract is the deliverable. It is served today by route handlers in this
 * app (which run the forward math server-side); it can later be served from the
 * exchange API without a consumer changing anything.
 *
 * Two contracts share these markets:
 *   - Weekly settle  (KX{chip}WS): settles Friday on the realized ORNN spot.
 *   - Monthly average (KX{chip}MS): settles on the month's average.
 *
 * Each forward price is the probability-weighted average of that contract's
 * "Above $K" strike ladder. See METHODOLOGY.md for the full derivation.
 */

/** Bumped when the forward math changes, so consumers can detect it. */
export const COMPUTE_METHODOLOGY_VERSION = '1.0';

export const COMPUTE_CHIPS = ['B200', 'H200', 'H100', 'A100', 'RTX5090'] as const;
export type ComputeChip = (typeof COMPUTE_CHIPS)[number];

/** Chip used when none is specified, or when a series ticker doesn't resolve. */
export const DEFAULT_COMPUTE_CHIP: ComputeChip = 'B200';

export function isComputeChip(value: string): value is ComputeChip {
    return (COMPUTE_CHIPS as readonly string[]).includes(value);
}

/** A realized (observed) price: ORNN spot, or a settled contract's expiration value. */
export interface ComputeSpotPoint {
    /** Unix seconds. */
    ts: number;
    /** Realized price, $/GPU-hr. */
    value: number;
    /** Event ticker, when the point is a settled contract. */
    ticker?: string | null;
    /** Short label, e.g. "W27". */
    label?: string | null;
}

/** One node on the forward curve, derived from a contract's live strike ladder. */
export interface ComputeForwardPoint {
    /** Unix seconds. Weeklies use their settle date; monthlies the middle of the month they cover. */
    expiry_ts: number;
    contract: 'weekly' | 'monthly';
    ticker: string | null;
    /** Short label, e.g. "W28" or "Aug 2026". */
    label: string;
    /** Forward price, $/GPU-hr. */
    price: number;
}

/** Response shape of GET /api-app/compute/forward-curve/{chip}. */
export interface ComputeCurveSnapshot {
    chip: ComputeChip;
    /** Unix seconds. */
    data_as_of: number;
    unit: 'usd_per_gpu_hour';
    methodology_version: string;
    /**
     * Last OBSERVED price. Never interpolated up to now: the ORNN series trails a
     * few days and weeklies only settle on Fridays, so an inferred value here would
     * be market expectation dressed up as reported spot.
     */
    spot: (ComputeSpotPoint & { source: 'ornn_index' }) | null;
    /**
     * The curve, ascending by expiry. SPARSE BY DESIGN: a contract whose ladder we
     * can't trust (too few strikes, or too much probability mass in an open tail) is
     * omitted rather than given an invented price. RTX5090 currently publishes only
     * 2 of 5 weeklies for exactly this reason, so consumers must handle holes.
     */
    forward: ComputeForwardPoint[];
    /** Already-settled weeklies, ascending. Realized, so they sit on the past side. */
    settled: ComputeSpotPoint[];
}

/** Response shape of GET /api-app/compute/forward-curve/{chip}/history. */
export interface ComputeCurveHistory {
    chip: ComputeChip;
    unit: 'usd_per_gpu_hour';
    /** Realized ORNN spot, ascending by ts. */
    points: ComputeSpotPoint[];
}

const BASE = '/api-app/compute/forward-curve';

async function fetchJson<T>(path: string, signal?: AbortSignal): Promise<T> {
    // Same-origin call to our own route handler, so no AWS-WAF token wrapper is
    // needed (that's for direct cross-origin calls to the exchange API).
    // eslint-disable-next-line no-restricted-globals
    const res = await fetch(path, { signal });
    if (!res.ok) throw new Error(`Compute forward curve API error ${res.status} for ${path}`);
    return res.json() as Promise<T>;
}

export async function fetchComputeCurveSnapshot(
    chip: ComputeChip,
    signal?: AbortSignal,
): Promise<ComputeCurveSnapshot> {
    return fetchJson<ComputeCurveSnapshot>(`${BASE}/${chip}`, signal);
}

export async function fetchComputeCurveHistory(chip: ComputeChip, signal?: AbortSignal): Promise<ComputeCurveHistory> {
    return fetchJson<ComputeCurveHistory>(`${BASE}/${chip}/history`, signal);
}
